// Bro: Potree-værktøj -> vært-UI (PointViews frontend) -> bruger svarer -> værktøjet fortsætter.
//
// Retning (se frontendens VIEWER-UI-OVERLAYS.md): almindelige modale dialoger (spørg om en værdi, bekræft, vælg) renderes af
// frontend/Vue, så de får PointViews fælles overlay-design - mens Potree fortsat ejer værktøjets logik, geometri, picking,
// live preview og lifecycle. Potree beskriver kun dialogen som DATA og får et svar tilbage.
//
// Integrationsformen følger den eksisterende konvention: frontenden hænger en lille vært-API på viewer-objektet
// (som `viewer.pvIfc`): `viewer.pvUi = { requestDialog(spec, signal) -> Promise<result> }`. Potree importerer INTET fra
// Vue. Findes værten ikke (standalone Potree, eksempler), beholder værktøjet sin egen DOM-dialog som fallback.
//
// spec (ren data, serialiserbar):
//   kind 'form'    (standard): { size: 's'|'m'|'l', title, text?, fields: [{id, type: 'number'|'text', label, value?, placeholder?,
//                    step?, min?, minExclusive?, quickValues?: [{label, value}]}], invalidMessage?, confirmLabel?, cancelLabel?,
//                    destructive?, escapeCancels? }
//   kind 'confirm': { size, title, text, confirmLabel, cancelLabel?, destructive?, escapeCancels? }   - ingen felter
//   kind 'message': { size, title, text, confirmLabel? }                                              - kun "Luk", ingen sideeffekt
//   kind 'offset':  { size, title, text?, tabs: [{id, label, presetLabel, presets: [{label, valueCm, info?}], wall?: {label, minusLabel, plusLabel}}],
//                    locale, offsetLabel, downLabel, upLabel, wallInvalidMessage, resetLabel, confirmLabel, cancelLabel? }
//                    - lodret forskydning: et valg udfylder feltet, tab.wall regnes med eget fortegn, retning op/ned; values: {offsetCm: <tal med fortegn>}
//   text: ren tekst; linjeskift bevares.
//   field.quickValues: knapper, der KUN udfylder feltet (bekræfter aldrig selv).
//   field.min (+ minExclusive) + spec.invalidMessage: værten blokerer "bekræft" og viser invalidMessage, til feltet er gyldigt.
//   Værktøjs-prompts (spec.escapeCancels): klik udenfor lukker IKKE; Esc afbryder værktøjet. Uden escapeCancels (almindelig
//   bekræft/besked): ikke persistent - Esc/udenfor = annullér uden sideeffekt. Formularer er altid persistent.
// signal: AbortSignal - afbryder værktøjet dialogen (fx `cancel_insertions`), lukker værten dialogen uden svar.
// resultat: { action: 'confirm', values: {<fieldId>: <rå tekst>} } | { action: 'cancel' }

export function hostDialogAvailable(viewer){
	return !!(viewer && viewer.pvUi && typeof viewer.pvUi.requestDialog === 'function');
}

// Beder værten vise dialogen. Resolver altid (aldrig reject): 'cancel' ved annullér, ×, Esc (hvis spec.escapeCancels),
// afbrudt signal eller værts-fejl - så værktøjet kan behandle alle "ikke bekræftet"-udfald ens.
export function requestHostDialog(viewer, spec, signal){
	// så digitaliseringens højreklik-i-idle-regel ved, at en dialog er åben (værts-dialogen er ikke modal over canvas'et)
	const dm = viewer && viewer.digitizationModule;
	if(dm && typeof dm._hostDialogOpened === 'function') dm._hostDialogOpened();
	return Promise.resolve()
		.then(() => viewer.pvUi.requestDialog(spec, signal))
		.then((result) => (result && result.action === 'confirm') ? result : {action: 'cancel'}, () => ({action: 'cancel'}))
		.then((result) => {
			if(dm && typeof dm._hostDialogClosed === 'function') dm._hostDialogClosed();
			return result;
		});
}

// Værktøjs-prompt (en dialog, der hører til et aktivt værktøj/en menuhandling): samme livscyklus som de indbyggede
// DOM-dialoger - "cancel_insertions" (Esc udenfor dialogen, værktøjet skiftet væk) afbryder dialogen via AbortSignal, og
// listeneren fjernes igen når dialogen er afgjort. Esc i dialogen afbryder værktøjet (escapeCancels).
export function requestHostToolPrompt(viewer, spec){
	const controller = new AbortController();
	const onCancelEvent = () => controller.abort();
	viewer.addEventListener('cancel_insertions', onCancelEvent);
	return requestHostDialog(viewer, Object.assign({escapeCancels: true}, spec), controller.signal)
		.then((result) => { viewer.removeEventListener('cancel_insertions', onCancelEvent); return result; });
}

// Almindelig besked/bekræftelse (ikke en del af et værktøj): ingen signal, ikke persistent (se spec ovenfor).
export function requestHostConfirm(viewer, spec){
	return requestHostDialog(viewer, spec);
}

// Ren tekst til en besked, der i standalone-DOM-dialogen er skrevet som HTML (<br>, <b>).
export function htmlMessageToText(html){
	return String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
}
